import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLayoutEffect, useRef } from 'react';
import { Link2, Plus, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import { answerTypeLabel } from './answer-type-labels';
import { FormItemIcon } from './items/FormItemIcon';
import { ItemBlock } from './items/ItemBlock';
/**
 * Block-editor for Form Node items. Visually unified — reads as one
 * continuous document, no per-row borders. Still functionally many blocks:
 *   - Enter commits the block + appends a new one.
 *   - Backspace at start of an empty block deletes it + focuses prior.
 *   - Tab / Shift+Tab adjust nesting (depth 0 ↔ 1).
 *   - Blur fires inference on the just-left block.
 *   - Multi-line paste splits into N blocks.
 */
export function FormItemBlockList({ items, inferringIds, touchedIds, variables, onTextChange, onCommitAndAppend, onAppendItem, onDeleteEmpty, onPasteSplit, onBlurBlock, onOpenItem, onIndent, onOutdent, onFocusPrev, onFocusNext, focusItemId, onFocusConsumed, }) {
    const containerRef = useRef(null);
    const refs = useRef(new Map());
    const numbering = computeNumbering(items);
    useLayoutEffect(() => {
        if (!focusItemId)
            return;
        const el = refs.current.get(focusItemId);
        if (!el)
            return;
        el.focus();
        const length = el.value.length;
        try {
            el.setSelectionRange(length, length);
        }
        catch {
            // 0-length textareas throw in some browsers; harmless
        }
        onFocusConsumed();
    }, [focusItemId, onFocusConsumed]);
    return (_jsxs("div", { ref: containerRef, className: "rounded-md border border-border/60 bg-background/40 px-2 py-1.5", children: [items.map((item, index) => (_jsx(ItemBlock, { item: item, label: numbering[index] ?? `${index + 1}`, hasNext: index < items.length - 1, inferring: inferringIds.has(item.id), placeholder: "Write a question or statement\u2026", chips: _jsx(FormItemChips, { item: item, touched: touchedIds.has(item.id) }), icon: _jsx(FormItemIcon, { item: item }), variables: variables, registerRef: el => {
                    if (el)
                        refs.current.set(item.id, el);
                    else
                        refs.current.delete(item.id);
                }, onTextChange: text => onTextChange(item.id, text), onCommit: () => onCommitAndAppend(item.id), onDeleteEmpty: () => onDeleteEmpty(item.id), onPasteSplit: lines => onPasteSplit(item.id, lines), onIndent: () => onIndent(item.id), onOutdent: () => onOutdent(item.id), onFocusPrev: () => onFocusPrev(item.id), onFocusNext: () => onFocusNext(item.id), onBlurBlock: () => {
                    // Defer so clicks within the list don't register as blur.
                    window.setTimeout(() => {
                        const active = document.activeElement;
                        if (containerRef.current &&
                            active instanceof HTMLElement &&
                            containerRef.current.contains(active)) {
                            return;
                        }
                        onBlurBlock(item.id);
                    }, 0);
                }, onOpen: () => onOpenItem(item.id) }, item.renderKey ?? item.id))), _jsxs("button", { type: "button", onClick: onAppendItem, className: "mt-1 flex w-full items-center gap-1 rounded-sm px-1 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors", children: [_jsx(Plus, { className: "h-3 w-3" }), items.length === 0 ? 'Add the first item' : 'Add item'] })] }));
}
/** The id, the type (or Statement), the memory binding and Required, as chips. */
function FormItemChips({ item, touched, }) {
    return (_jsxs(_Fragment, { children: [_jsx("span", { className: "rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground select-none", children: item.id }), _jsxs("span", { className: cn('inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-medium', touched
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'), children: [!touched && _jsx(Sparkles, { className: "h-2 w-2" }), item.kind === 'question'
                        ? answerTypeLabel(item.answerType)
                        : 'Statement'] }), item.kind === 'question' && item.scratchpadMapping && (_jsxs("span", { className: "inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary select-none", title: `Mapped from memory: ${item.scratchpadMapping.path}`, children: [_jsx(Link2, { className: "h-2 w-2" }), item.scratchpadMapping.path] })), item.kind === 'question' && item.required && (_jsx("span", { className: "rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary select-none", children: "Required" })), touched && (_jsx("span", { className: "text-[9px] text-muted-foreground select-none", children: "edited" }))] }));
}
/**
 * Compute per-item labels given the ordered items: top-level items get
 * 1/2/3, nested items get Na/Nb/… under their nearest preceding top-level.
 * A nested item that appears before any top-level falls back to its index
 * (defensive; the editor won't let it happen).
 */
function computeNumbering(items) {
    const labels = [];
    let top = 0;
    let subs = 0;
    for (const item of items) {
        const depth = item.depth ?? 0;
        if (depth === 0) {
            top += 1;
            subs = 0;
            labels.push(`${top}`);
        }
        else {
            subs += 1;
            labels.push(top === 0 ? `${subs}` : `${top}${toAlpha(subs)}`);
        }
    }
    return labels;
}
function toAlpha(n) {
    // 1 → 'a', 2 → 'b', … 26 → 'z', 27 → 'aa' (rare)
    let s = '';
    let value = n;
    while (value > 0) {
        value -= 1;
        s = String.fromCharCode(97 + (value % 26)) + s;
        value = Math.floor(value / 26);
    }
    return s;
}
//# sourceMappingURL=FormItemBlockList.js.map